import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from '../../RootStore';
import { userManagementService } from '../../../services/modules/userManagement/userManagement.service';
import {
  IUserManagementParam,
  IUserManagementResult,
} from '../../../models/user-management/UserManagementModel';
import { toast } from 'react-toastify';
import { GridApi } from 'ag-grid-community';

interface ODataParams {
  $filter?: string;
  $orderby?: string;
  $skip?: number;
  $top?: number;
  $select?: string;
  $count?: boolean;
}

interface GridParams {
  startRow: number;
  endRow: number;
  filterModel: any;
  sortModel: any;
}

export class UserManagementStore {
  rootStore: RootStore;
  selectedUser: IUserManagementResult | null = null;
  error: string | null = null;
  gridApi: GridApi<IUserManagementResult> | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  setGridApi = (api: GridApi<IUserManagementResult>) => {
    this.gridApi = api;
  };

  refreshGrid = () => {
    if (this.gridApi) {
      this.gridApi.refreshServerSide();
      this.gridApi.refreshInfiniteCache();
    }
  };

  setSelectedUser = (user: IUserManagementResult | null) => {
    this.selectedUser = user;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  listUsers = async (
    params: GridParams
  ): Promise<{ rows: IUserManagementResult[]; lastRow: number; count: number }> => {
    try {
      const odataParams: ODataParams = {
        $count: true,
        $skip: params.startRow,
        $top: params.endRow - params.startRow,
      };

      if (params.filterModel) {
        const filters = Object.entries(params.filterModel).map(([field, filter]: [string, any]) => {
          const numericFields = ['id'];
          if (numericFields.includes(field)) {
            return `${field} eq ${filter.filter}`;
          }
          const value = String(filter.filter ?? '').toLowerCase().replace(/'/g, "''");
          return `contains(tolower(${field}), '${value}')`;
        });

        if (filters.length > 0) {
          odataParams.$filter = filters.join(' and ');
        }
      }

      if (params.sortModel && params.sortModel.length > 0) {
        const { colId, sort } = params.sortModel[0];
        odataParams.$orderby = `${colId} ${sort === 'asc' ? 'asc' : 'desc'}`;
      }

      const response: any = await userManagementService.listUsers({
        params: odataParams,
      });

      return runInAction(() => {
        if (response) {
          const totalCount = response['@odata.count'] || 0;
          return {
            rows: response.value || [],
            lastRow: totalCount,
            count: totalCount,
          };
        }
        return {
          rows: [],
          lastRow: 0,
          count: 0,
        };
      });
    } catch (error) {
      console.error('Error fetching users:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'An unknown error occurred';
      });
      throw error;
    }
  };

  deleteUser = async (id: string) => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response: any = await userManagementService.deleteUser(id);

      if (response.error) {
        runInAction(() => {
          this.error = response.message;
        });
        return response;
      }

      runInAction(() => {
        this.error = null;
        this.refreshGrid();
      });

      toast.success('Kullanıcı başarıyla silindi');
      return response;
    } catch (error) {
      console.error('Silme hatası:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Bilinmeyen bir hata oluştu';
        toast.error('Silme sırasında bir hata oluştu');
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  };

  updateUser = async (id: string, data: Partial<IUserManagementParam>) => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response: any = await userManagementService.updateUser(id, data);

      if (response.error) {
        runInAction(() => {
          this.error = response.message;
        });
        return response;
      }

      runInAction(() => {
        this.error = null;
        this.refreshGrid();
      });
      return response;
    } catch (error) {
      console.error('Güncelleme hatası:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'Bilinmeyen bir hata oluştu';
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  };
  getRoles = async () => {
    try {
      return await userManagementService.getRoles();
    } catch (error) {
      console.error('Error fetching roles:', error);
      throw error;
    }
  };
}
